import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useSite } from '../useSite';
import { AUDIENCE_KEYS, findPage, PATHS } from '../routes';
import { BrandLockup } from './Brand';
import { ButtonLink } from './Primitives';

export const LangSwitch = ({ testId = 'lang-switch' }) => {
  const { pathname, hash } = useLocation();
  const { lang } = useSite();
  const page = findPage(pathname);
  const target = (l) => {
    const base = page ? PATHS[page.key][l] : PATHS.home[l];
    if (page?.key === 'home' && (hash === '#testimonials' || hash === '#comentarios')) {
      return `${base}#${l === 'es' ? 'comentarios' : 'testimonials'}`;
    }
    return base;
  };
  return (
    <div className="flex items-center text-xs font-semibold tracking-[0.18em]" data-testid={testId}>
      {['en', 'es'].map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <span className="mx-2 text-tan" aria-hidden="true">|</span>}
          <Link
            to={target(l)}
            hrefLang={l}
            lang={l}
            aria-current={lang === l ? 'true' : undefined}
            data-testid={`${testId}-${l}`}
            className={lang === l ? 'text-kombu-green' : 'text-cafe-noir/50 hover:text-kombu-green'}
          >
            {l.toUpperCase()}
          </Link>
        </React.Fragment>
      ))}
    </div>
  );
};

const Dropdown = ({ label, items, testId }) => (
  <div className="group relative" data-testid={testId}>
    <button type="button" className="flex items-center gap-1 py-6 text-sm font-medium text-cafe-noir hover:text-kombu-green" aria-haspopup="true">
      {label}
      <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180 group-focus-within:rotate-180" />
    </button>
    <div className="invisible absolute left-1/2 top-full z-50 w-80 -translate-x-1/2 border border-bone bg-white p-2 opacity-0 shadow-[0_18px_40px_-20px_rgba(53,64,36,0.35)] transition-opacity duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
      {items.map((it) => (
        <Link key={it.to} to={it.to} onClick={() => document.activeElement?.blur()} data-testid={`${testId}-${it.key}`} className="block px-4 py-3 text-sm text-cafe-noir hover:bg-bone-light hover:text-kombu-green">
          {it.label}
        </Link>
      ))}
    </div>
  </div>
);

const useNavItems = () => {
  const { t, p } = useSite();
  return {
    audiences: AUDIENCE_KEYS.map((k) => ({ key: k, to: p(k), label: t.audiences[k].name })),
    links: [
      { key: 'services', to: p('expertise'), label: t.nav.expertise },
      { key: 'plans', to: p('plans'), label: t.nav.plans },
      { key: 'about', to: p('about'), label: t.nav.about },
    ],
  };
};

const MobileMenu = ({ onClose }) => {
  const { t, p } = useSite();
  const nav = useNavItems();
  const group = (title, items) => (
    <div className="border-b border-tan/40 py-5">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-moss-green">{title}</p>
      {items.map((it) => (
        <Link key={it.to} to={it.to} onClick={onClose} className="block py-2 font-serif text-xl text-kombu-green">{it.label}</Link>
      ))}
    </div>
  );
  return (
    <div className="fixed inset-0 top-[68px] z-40 overflow-y-auto bg-bone-light px-5 pb-10 lg:hidden" data-testid="mobile-menu">
      {group(t.nav.whoWeServe, nav.audiences)}
      <div className="py-5">
        {nav.links.map((it) => (
          <Link key={it.to} to={it.to} onClick={onClose} data-testid={`mobile-nav-${it.key}`} className="block py-2 font-serif text-xl text-kombu-green">{it.label}</Link>
        ))}
      </div>
      <ButtonLink to={p('book')} testId="mobile-nav-book" className="w-full">{t.nav.book}</ButtonLink>
    </div>
  );
};

export const Header = () => {
  const { t, p } = useSite();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const nav = useNavItems();
  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-bone bg-bone-light/95 backdrop-blur-sm" data-testid="site-header">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:bg-white focus:px-3 focus:py-2">{t.nav.skip}</a>
      <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-5 md:px-8 lg:h-auto">
        <Link to={p('home')} aria-label="BULLISH Tax & Advisory" data-testid="header-logo-link">
          <BrandLockup />
        </Link>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main">
          <Dropdown label={t.nav.whoWeServe} items={nav.audiences} testId="nav-who-we-serve" />
          {nav.links.map((it) => (
            <Link key={it.key} to={it.to} data-testid={`nav-${it.key}`} className={`text-sm font-medium hover:text-kombu-green ${pathname === it.to ? 'text-kombu-green' : 'text-cafe-noir'}`}>
              {it.label}
            </Link>
          ))}
          <LangSwitch />
          <ButtonLink to={p('book')} testId="nav-book-btn" className="!px-5 !py-3">{t.nav.book}</ButtonLink>
        </nav>
        <div className="flex items-center gap-4 lg:hidden">
          <LangSwitch testId="mobile-lang-switch" />
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? t.nav.close : t.nav.menu} data-testid="mobile-menu-toggle" className="p-1 text-kombu-green">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
    </header>
    {open && <MobileMenu onClose={() => setOpen(false)} />}
    </>
  );
};
